import React from 'react';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import * as utils from 'common/utils';
import {
  findAlarmParameterForFeature,
  hasAlarmParameters
} from 'home/AssetHierarchy/utils/alarmParameters';
import * as helpers from 'common/components/Chart/utils/helpers';
import ChartOptions from 'common/components/Chart/components/ChartOptions';
import ChartTab from 'common/components/Chart/components/ChartTab';

function buildMenuItems(ctx) {
  const {
    hasData,
    showCharts,
    chartType,
    amp_type,
    config,
    tag,
    addingNote,
    editTrend,
    openFeature,
    editDemod,
    editTagInfo,
    editRULSettings,
    openFeatureDetails,
    toggleAddingNote,
    refreshChart,
    downloadSpectrumChart,
    edit
  } = ctx;

  return [
    {
      text: 'addNote',
      onClick: toggleAddingNote,
      disabled: (
        !hasData ||
        !showCharts ||
        chartType === 'waterfall_spectrum' ||
        amp_type === 'demod' ||
        chartType === 'TSW' ||
        (config && !config.default)) ||
        (tag && tag.type === 'utilization'),
      active: addingNote
    },
    {
      text: 'refresh',
      onClick: refreshChart,
      disabled: !showCharts,
      active: false
    },
    {
      text: 'download',
      // Delegate to ChartsView.downloadSpectrumChart, which derives the export
      // units and data together (deriveSpectrumDisplay) so the downloaded file
      // can never pair converted units with unconverted data (SC-36397).
      onClick: downloadSpectrumChart,
      disabled: chartType === 'waterfall_spectrum' || !showCharts,
      active: false
    },
    {
      text: 'editChart',
      onClick: edit,
      disabled: chartType === 'waterfall_spectrum' || openFeatureDetails || (tag && tag.type === 'utilization'),
      active: editTrend || openFeature || editDemod || editTagInfo || editRULSettings
    }
  ];
}

export function buildTabItems(ctx) {
  const {
    t,
    hasData,
    showCharts,
    chartType,
    amp_type,
    config,
    tag,
    envelopeAlarm,
    openOverallAlarm,
    editTrend,
    openFeature,
    editDemod,
    editTagInfo,
    editRULSettings,
    feature,
    measurement_type,
    logScale,
    toggleAddingNote,
    toggleCalendarExpand,
    refreshChart,
    downloadSpectrumChart,
    state,
    openEnvelopeAlarm,
    toggleOverallAlarm,
    openEditTrend,
    openAddEditFeatures,
    openEditDemod,
    openBaselinePage,
    openEditTagInfo,
    openRULSettings,
    isComponentRULEnabled
  } = ctx;


  // Whether a measure can carry an overall alarm is now a question about the
  // data, not a list of feature names: the tag says which measures have an
  // alarm parameter, and the answer changes when someone enables one. Demod keeps
  // its own entry because it is a separate menu item, not a separate rule.
  // Only the charts that pick a feature can say which measure is on screen.
  // A scalar tag type - temperature, pressure - shows its one measure without
  // naming it, so there the question is whether the tag type has any alarm
  // measure at all. Asking the per-measure question there would disable an
  // alarm that has always worked.
  // A tag response without alarm_parameters comes from a backend older than
  // this screen; there the historic rule stands rather than every alarm being
  // disabled with a reason that is not true.
  const knowsMeasures = hasAlarmParameters(tag);
  const alarmParameter = feature && knowsMeasures
    ? findAlarmParameterForFeature(tag, amp_type, feature)
    : null;
  let hasAlarmMeasure;
  if (!knowsMeasures) {
    hasAlarmMeasure = !tag || (
      (tag.type !== 'vibration' || (
        feature === 'rms' && _.includes(['velocity', 'acceleration', 'demod'], amp_type)
      )) &&
      (tag.type !== 'current' || feature === 'rms')
    );
  } else if (feature) {
    hasAlarmMeasure = Boolean(alarmParameter);
  } else {
    hasAlarmMeasure = hasAlarmParameters(tag);
  }
  const alarmBlocked = (
    !hasData || !tag ||
    (config && !config.default) ||
    (tag.type === 'vibration' && measurement_type !== 1) ||
    !hasAlarmMeasure
  );
  // Demod is a separate menu item for vibration only; a current tag's demod
  // chart has no such item, so its overall alarm stays where it always was.
  const isVibrationDemod = Boolean(tag) && tag.type === 'vibration' && amp_type === 'demod';
  const overallAlarmDisabled = Boolean(
    alarmBlocked || (!showCharts && !openOverallAlarm) || isVibrationDemod
  );
  // Say which of the two reasons applies. "No alarm parameter" is an admin task and
  // not a chart setting, so the generic text sent people looking in the wrong
  // place - and it is the reason that follows a measure whose derived line the
  // chart no longer draws.
  const noAlarmParameterHere = Boolean(knowsMeasures && feature && !alarmParameter);
  const overallAlarmReason = noAlarmParameterHere
    ? t('charts.tabs.alarms.noAlarmParameterReason', {
      defaultValue: 'No alarm parameter exists for this measure, so it shows no alarm lines. A Petasense admin can add one under Admin Dashboard \u2192 Alarm Parameters.'
    })
    : t('charts.tabs.alarms.overallAlarmDisabledReason', {
      defaultValue: 'An overall alarm has to be enabled for this measure. Full Bandwidth only.'
    });
  const demodAlarmDisabled = Boolean(
    alarmBlocked || (!showCharts && !openOverallAlarm) || !isVibrationDemod
  );
  // An envelope alarm needs a spectrum parameter, and only vibration and
  // current have one (vibration_spectrum_velocity,
  // vibration_spectrum_acceleration, rms_current_spectrum). Every other vector
  // tag type - magnetic flux, ultrasound - showed the item enabled and had
  // nothing to save it to. An older backend keeps the historic rule.
  const hasEnvelopeAlarm = Boolean(tag) && (
    !knowsMeasures || _.includes(['vibration', 'current'], tag.type)
  );
  const envelopeAlarmDisabled = Boolean(
    !hasData || !tag || !hasEnvelopeAlarm ||
    (config && !config.default) ||
    tag.structure_type !== 'vector' ||
    !_.includes(['velocity', 'acceleration', 'normal'], amp_type) ||
    (!showCharts && !envelopeAlarm) ||
    measurement_type === 0 ||
    (tag.type === 'current' && !logScale)
  );

  return [
    {
      text: t('charts.tabs.general.title', { defaultValue: 'General' }),
      // Stable identity for the open/closed dropdown check; `text` is localized.
      value: 'General',
      onClick: () => ctx.toggleShowTabOptions('General'),
      dropdown: [
        {
          text: t('charts.tabs.general.addNote'),
          onClick: () => toggleAddingNote(),
          disabled: (
            !hasData ||
            !showCharts ||
            chartType === 'waterfall_spectrum' ||
            amp_type === 'demod' ||
            chartType === 'TSW' ||
            (tag && tag.type === 'utilization') ||
            (config && !config.default)
          ),
          id: 4
        },
        {
          text: t('machineDashboard.health.charts.tabsDropdown.calendar'),
          onClick: () => toggleCalendarExpand(),
          id: 1,
          disabled: !showCharts
        },
        {
          text: t('charts.tabs.general.refresh'),
          onClick: () => refreshChart(),
          id: 2,
          disabled: !showCharts
        },
        {
          text: t('charts.tabs.general.download'),
          // Spectrum needs unit-converted data (SC-36397); trend/waveform don't.
          onClick: () => (chartType === 'spectrum'
            ? downloadSpectrumChart()
            : helpers.downloadChartData(state)),
          id: 3,
          disabled: !hasData || !tag || chartType === 'waterfall_spectrum' || !showCharts
        }
      ]
    },
    {
      text: t('charts.tabs.alarms.title', { defaultValue: 'Alarms' }),
      value: 'Alarms',
      // Hidden by tag type, unless the server says the type now carries alarm
      // measures. Enabling an alarm parameter is then what makes the tab appear,
      // rather than a separate edit here. Deliberately additive: nothing that
      // shows today becomes hidden.
      hidden: (
        tag
        && !hasAlarmParameters(tag)
        && (_.includes(['utilization', 'shaft_speed', 'magnetic_flux'], tag.type))
      ),
      onClick: () => ctx.toggleShowTabOptions('Alarms'),
      dropdown: [
        {
          text: t('machineDashboard.health.charts.tabsDropdown.envelopeAlarm'),
          selected: envelopeAlarm,
          onClick: () => openEnvelopeAlarm(),
          disabled: envelopeAlarmDisabled,
          hidden: !tag || tag.structure_type !== 'vector' || !hasEnvelopeAlarm,
          // One condition for both, after the two drifted: the tooltip omitted
          // acceleration and tested measurement_type differently, so an
          // enabled control claimed it could not be used.
          title: envelopeAlarmDisabled
            ? t('charts.tabs.alarms.envelopeAlarmDisabledReason', {
              defaultValue: 'Envelope Alarms can be set for Full Bandwidth - Velocity and Acceleration features'
            }) : null,
          id: 7
        },
        {
          text: t('charts.tabs.alarms.overallAlarm'),
          selected: openOverallAlarm,
          onClick: () => toggleOverallAlarm(),
          disabled: overallAlarmDisabled,
          title: overallAlarmDisabled ? overallAlarmReason : null,
          id: 4
        },
        {
          text: t('charts.tabs.alarms.demodAlarm', { defaultValue: 'Demod Alarm' }),
          selected: openOverallAlarm,
          onClick: () => toggleOverallAlarm(),
          disabled: demodAlarmDisabled,
          title: demodAlarmDisabled
            ? t('charts.tabs.alarms.demodAlarmDisabledReason', {
              defaultValue: 'A demod alarm has to be enabled for this measure. Full Bandwidth only.'
            }) : null,
          hidden: !tag || tag.type !== 'vibration',
          id: 5
        }
      ]
    },
    {
      text: t('charts.tabs.edit.title', { defaultValue: 'Edit' }),
      value: 'Edit',
      hidden: (tag && tag.type === 'utilization'),
      onClick: () => ctx.toggleShowTabOptions('Edit'),
      dropdown: [
        {
          text: t('machineDashboard.health.charts.tabsDropdown.chart', { defaultValue: 'Chart' }),
          selected: editTrend,
          onClick: () => openEditTrend(),
          disabled: (!showCharts && !editTrend),
          id: 1
        },
        {
          text: t('charts.tabs.edit.spectrumFeature', { defaultValue: 'Spectrum Feature' }),
          selected: openFeature,
          onClick: () => openAddEditFeatures(),
          disabled: !hasData || !tag || (!showCharts && !openFeature) || (config && !config.default) || measurement_type === 0,
          hidden: !tag || tag.structure_type !== 'vector',
          id: 2
        },
        {
          text: t('charts.tabs.edit.demodSpectrum', { defaultValue: 'Demod Spectrum' }),
          selected: editDemod,
          onClick: () => openEditDemod(),
          disabled: !tag || (!showCharts && !editDemod) || (config && !config.default) || measurement_type === 0,
          hidden: !tag || !utils.isDemodSupported(tag.type),
          id: 3
        },
        {
          text: t('charts.tabs.edit.setBaseline', { defaultValue: 'Set Baseline' }),
          onClick: openBaselinePage,
          disabled: tag.active === false,
          id: 4
        },
        {
          text: t('charts.tabs.edit.tagInfo', { defaultValue: 'Tag Info' }),
          selected: editTagInfo,
          onClick: () => openEditTagInfo(),
          disabled: (!showCharts && !editTagInfo),
          id: 5
        },
        {
          text: t('machineDashboard.health.settings.headers.rulSettings', { defaultValue: 'RUL Settings' }),
          selected: editRULSettings,
          onClick: openRULSettings,
          hidden: !tag || tag.type !== 'vibration' || (chartType !== 'trend' && chartType !== 'TSW') || !isComponentRULEnabled(),
          id: 6
        }
      ]
    }
  ];
}

function buildViewItems(ctx) {
  const { t, chartType, setChartType, isHidden } = ctx;

  return [
    {
      text: 'Tr',
      fullText: t('charts.view.trend', { defaultValue: 'Trend' }),
      active: chartType === 'trend',
      onClick: () => setChartType('trend'),
      hidden: isHidden('trend'),
      id: 1
    },
    {
      text: 'Sp',
      fullText: t('charts.view.spectrum', { defaultValue: 'Spectrum' }),
      active: chartType === 'spectrum',
      onClick: () => setChartType('spectrum'),
      hidden: isHidden('spectrum'),
      id: 2
    },
    {
      text: 'Wa',
      fullText: t('charts.view.waveform', { defaultValue: 'Waveform' }),
      active: chartType === 'waveform',
      onClick: () => setChartType('waveform'),
      hidden: isHidden('waveform'),
      id: 3
    },
    {
      text: 'Co',
      fullText: t('machineDashboard.health.charts.viewModes.combined', { defaultValue: 'Combined (T,S,W)' }),
      active: chartType === 'TSW',
      onClick: () => setChartType('TSW'),
      hidden: isHidden('TSW'),
      id: 5
    },
    {
      text: 'Wf',
      fullText: t('machineDashboard.health.charts.viewModes.waterfallSpectrum', { defaultValue: 'Waterfall Spectrum' }),
      active: chartType === 'waterfall_spectrum',
      onClick: () => setChartType('waterfall_spectrum'),
      hidden: isHidden('waterfall_spectrum'),
      id: 6
    },
    {
      text: 'Ce',
      fullText: t('machineDashboard.health.charts.viewModes.cepstrum', { defaultValue: 'Cepstrum' }),
      active: chartType === 'cepstrum',
      onClick: () => setChartType('cepstrum'),
      hidden: isHidden('cepstrum'),
      id: 7
    }
  ];
}

const ChartsViewToolbar = (props) => {
  const {
    addingNote,
    bin_nos,
    calendarExpanded,
    chartType,
    config,
    customDayFrom,
    customDayTo,
    days,
    editDemod,
    editTrend,
    envelopeAlarm,
    feature,
    filterMax,
    filterMin,
    loadingWaterfall,
    logScale,
    measurement_type,
    menuItemsContext,
    number_of_spectrums,
    openFeature,
    openFeatureDetails,
    openOverallAlarm,
    selectedTabItem,
    showBaseline,
    showOverlay,
    tag,
    amp_type,
    filteredOverlayPoints,
    toggleShowTabOptions,
    calendarSelect,
    toggleCalendarExpand,
    handleSpectraChange,
    getChartData,
    setSpeedFilterValues,
    refreshChart,
    toggleShowBaseline,
    toggleShowOverlay,
    updateSelectedPoints,
    retryOverlayTraceForPoint
  } = props;

  const menuItems = buildMenuItems(menuItemsContext);
  const tabItems = buildTabItems(menuItemsContext);
  const viewItems = buildViewItems(menuItemsContext);

  const disableBinFilter = editTrend || openFeature ||
    openFeatureDetails || editDemod || envelopeAlarm;
  const disableFilterOptions = disableBinFilter || openOverallAlarm;

  return (
    <>
      <ChartTab
        tabItems={tabItems}
        selectedTabItem={selectedTabItem}
        toggleShowTabOptions={toggleShowTabOptions}
      />
      <ChartOptions
        disableFilterOptions={disableFilterOptions}
        disableBinFilter={disableBinFilter}
        addingNote={addingNote}
        bin_nos={bin_nos}
        calendarSelect={calendarSelect}
        calendarExpand={toggleCalendarExpand}
        calendarExpanded={calendarExpanded}
        chartType={chartType}
        menuItems={menuItems}
        viewItems={viewItems}
        number_of_spectrums={number_of_spectrums}
        handleSpectraChange={handleSpectraChange}
        defaultChart={config.default}
        customDayFrom={customDayFrom}
        customDayTo={customDayTo}
        days={days}
        tag={tag}
        amp_type={amp_type}
        feature={feature}
        getChartData={getChartData}
        measurement_type={measurement_type}
        filterMin={filterMin}
        filterMax={filterMax}
        setSpeedFilterValues={setSpeedFilterValues}
        speedUnits={config && config.trends_data && config.trends_data[0] && config.trends_data[0].speed_units}
        refreshChart={refreshChart}
        showBaseline={showBaseline}
        loadingWaterfall={loadingWaterfall}
        toggleShowBaseline={toggleShowBaseline}
        logScale={logScale}
        showOverlay={showOverlay}
        toggleShowOverlay={toggleShowOverlay}
        selectedPoints={filteredOverlayPoints}
        updateSelectedPoints={updateSelectedPoints}
        retryOverlayTrace={retryOverlayTraceForPoint}
      />
    </>
  );
};

/* eslint-disable react/require-default-props */
ChartsViewToolbar.propTypes = {
  addingNote: PropTypes.bool,
  bin_nos: PropTypes.array,
  calendarExpanded: PropTypes.bool,
  chartType: PropTypes.string,
  config: PropTypes.object,
  customDayFrom: PropTypes.string,
  customDayTo: PropTypes.string,
  days: PropTypes.number,
  editDemod: PropTypes.bool,
  editTrend: PropTypes.bool,
  envelopeAlarm: PropTypes.bool,
  feature: PropTypes.string,
  filterMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  filterMin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loadingWaterfall: PropTypes.bool,
  logScale: PropTypes.bool,
  measurement_type: PropTypes.number,
  menuItemsContext: PropTypes.object,
  number_of_spectrums: PropTypes.number,
  openFeature: PropTypes.bool,
  openFeatureDetails: PropTypes.bool,
  openOverallAlarm: PropTypes.bool,
  selectedTabItem: PropTypes.string,
  showBaseline: PropTypes.bool,
  showOverlay: PropTypes.bool,
  tag: PropTypes.object,
  amp_type: PropTypes.string,
  filteredOverlayPoints: PropTypes.array,
  toggleShowTabOptions: PropTypes.func,
  calendarSelect: PropTypes.func,
  toggleCalendarExpand: PropTypes.func,
  handleSpectraChange: PropTypes.func,
  getChartData: PropTypes.func,
  setSpeedFilterValues: PropTypes.func,
  refreshChart: PropTypes.func,
  toggleShowBaseline: PropTypes.func,
  toggleShowOverlay: PropTypes.func,
  updateSelectedPoints: PropTypes.func,
  retryOverlayTraceForPoint: PropTypes.func
};

export default ChartsViewToolbar;
